var _span;
import React, { memo, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Cookies } from 'react-cookie';
import { useSelector } from 'react-redux';
import { useFeatures } from '@atc/features';
import { injectScript } from '@atc/dom-fns';
import { getFirstPartyVistorId, getPixallId } from '@atc/pixall-fns';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import _isEqual from 'lodash/isEqual';
import OwnerChatCta from '../OwnerChatCta/index.js';

/**
 * No-operation function used as a default callback.
 * @function noop
 */
import { jsx as _jsx } from "react/jsx-runtime";
const noop = () => {};

/**
 * Renders the button for chat with different styling based on UI context.
 * @param {object} props - The component props
 * @param {string} props.className - CSS class name to add to the button
 * @param {string} props.label - Custom label text for the button
 * @param {function} props.onChatClick - Callback function for clicking the chat button
 * @param {string} props.uiContext - UI context to render different styles of button ('iconButton', 'iconButtonStacked', 'button', 'link')
 * @param {boolean} props.isDealerNameRelocation - Flag to determine if dealer name relocation is enabled
 * @returns {JSX.Element} The rendered chat button component
 */
export const renderButton = _ref => {
  let {
    className,
    label,
    onChatClick,
    uiContext,
    isDealerNameRelocation,
    forceChatLaunch,
    ...props
  } = _ref;
  const handleButtonClick = () => {
    onChatClick();
    if (forceChatLaunch) {
      window?.ActivEngage?.spacontrol?.launchChat?.();
    }
  };
  return /*#__PURE__*/_jsx(OwnerChatCta, {
    className: className,
    label: label,
    onChatClick: handleButtonClick,
    uiContext: uiContext,
    isDealerNameRelocation: isDealerNameRelocation,
    ...props
  });
};

/**
 * Prepares chat data and visit data for the ActivEngage chat system.
 * @param {object} listing - The vehicle listing object containing vehicle information
 * @param {object} user - The user object containing location and other user data
 * @returns {object} An object containing chatData and visitData
 */
const prepareChatData = (listing, user) => {
  let chatData;
  if (listing) {
    chatData = {
      vehicleStatus: listing.listingType,
      vehicleMake: listing.make,
      vehicleModel: listing.model,
      vehicleYear: listing.year,
      vehicleVin: listing.vin,
      vehicleId: listing.id
    };
  }
  const {
    location = {}
  } = user;
  const {
    zip: zipcode
  } = location;
  const cookies = new Cookies();
  const consumerIP = cookies.get('pxa_ipv4');
  const pixallFirstPartyId = getFirstPartyVistorId();
  const pixallThirdPartyId = getPixallId() || 'nl';
  const visitData = {
    zipcode,
    pixallFirstPartyId,
    pixallThirdPartyId,
    IP: consumerIP
  };
  return {
    chatData,
    visitData
  };
};

/**
 * Populates chat attributes on the document body for ActivEngage integration.
 * @param {string} chatEnv - The chat environment ('production' or 'qa')
 * @param {number} merchantId - The merchant identifier
 * @param {boolean} hideStickyInvitation - Whether to hide sticky invitation
 * @param {string} aeAccountUrl - The ActivEngage account URL
 * @param {object} listing - The vehicle listing object
 * @param {object} user - The user object
 */
const populateChatAttributes = (chatEnv, merchantId, hideStickyInvitation, aeAccountUrl, listing, user) => {
  const {
    chatData,
    visitData
  } = prepareChatData(listing, user);
  const bodyElement = document.getElementsByTagName('body')[0];
  bodyElement.setAttribute('chat-account', `${merchantId}.${aeAccountUrl}`);
  bodyElement.setAttribute('chat-env', chatEnv);
  if (hideStickyInvitation) {
    bodyElement.setAttribute('chat-config-label', 'behindbutton');
  }
  if (chatData) {
    const chatDataString = JSON.stringify(chatData);
    bodyElement.setAttribute('chat-data', chatDataString);
  }
  if (!_isEmpty(visitData)) {
    const chatVisitDataString = JSON.stringify(visitData);
    bodyElement.setAttribute('chat-visitdata', chatVisitDataString);
  }
};

/**
 * Cleans up chat attributes from the document body.
 * Removes all ActivEngage-related attributes from the body element.
 */
const cleanupChatAttributes = () => {
  const bodyElement = document.getElementsByTagName('body')[0];
  ['chat-account', 'chat-env', 'chat-data', 'chat-visitdata', 'chat-config-label'].forEach(attr => bodyElement.hasAttribute(attr) && bodyElement.removeAttribute(attr));
};

/**
 * OwnerChatActivEngage Component
 *
 * A React component that integrates with ActivEngage chat system to provide
 * chat functionality for vehicle listings. It handles script injection,
 * chat data preparation, and attribute management for the ActivEngage widget.
 *
 * @component
 * @param {object} props - The component props
 * @param {string} [props.className] - CSS class name to apply to the chat button
 * @param {object} props.listing - Vehicle listing object containing vehicle information
 * @param {function} props.onChatClick - Callback function triggered when chat button is clicked
 * @param {string} props.merchantId - Merchant identifier for ActivEngage
 * @param {string} [props.uiContext] - UI context for button styling ('iconButton', 'iconButtonStacked', 'button', 'link')
 * @param {boolean} [props.showChatCTA=true] - Whether to show the chat call-to-action button
 * @param {boolean} [props.forceChatLaunch=false] - Whether to force launch the chat. This will call ActivEngage.spacontrol.launchChat(), which is used to launch chat from inside a modal
 * @returns {JSX.Element|null} The rendered component or null if script hasn't loaded
 */
const OwnerChatActivEngage = /*#__PURE__*/memo(_ref2 => {
  let {
    className,
    listing,
    onChatClick,
    merchantId,
    uiContext,
    showChatCTA,
    forceChatLaunch,
    ...rest
  } = _ref2;
  const [hasScript, setHasScript] = useState(false);
  const [isFirstLoad, setIsFirstLoad] = useState(!window.ActivEngage);
  const user = useSelector(state => state.user || {}, _isEqual);
  const activEngageUrl = useSelector(state => _get(state, 'configs.activEngageUrl', ''), _isEqual);
  const {
    is_production: [isProduction],
    chat: [, {
      hide_sticky_invitation: hideStickyInvitation,
      ae_account_url: aeAccountUrl
    }],
    dealer_name_relocation: [isDealerNameRelocationFlagEnabled]
  } = useFeatures(['is_production', 'chat', 'dealer_name_relocation']);
  const chatEnv = isProduction ? 'production' : 'qa';
  const isPrivateSeller = _get(listing, 'owner.privateSeller', false);
  const isDealerNameRelocation = isDealerNameRelocationFlagEnabled && !isPrivateSeller;
  window.AE = {};
  window.AE.onAvailable = noop;
  window.AE.onUnavailable = noop;
  window.AE.onLaunch = noop;

  // Handle direct hit or first SPA transition
  // Sets up data and injects script
  useEffect(() => {
    if (!window.ActivEngage) {
      populateChatAttributes(chatEnv, merchantId, hideStickyInvitation, aeAccountUrl, listing, user);
      injectScript(activEngageUrl).then(() => {
        setHasScript(true);
      }).catch(error => {
        if (window && window.newrelic) {
          window.newrelic.addPageAction('ThirdPartyError', {
            thirdPartyName: 'ActivEngage',
            component: 'OwnerChatActivEngage',
            message: 'Error injecting ActivEngage script',
            status: error?.status || 'unknown',
            url: window.location.href,
            error
          });
        }
      });
    } else {
      setHasScript(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handles all SPA transitions after first
  useEffect(() => {
    if (hasScript) {
      if (!isFirstLoad) {
        populateChatAttributes(chatEnv, merchantId, hideStickyInvitation, aeAccountUrl, listing, user);
        window.ActivEngage?.spacontrol?.run();
      } else {
        setIsFirstLoad(false);
      }
      // Always rebind on every load to ensure the chat button works correctly
      window.ActivEngage?.rebind?.();
      return () => {
        cleanupChatAttributes();
        window.ActivEngage?.spacontrol?.stop();
      };
    }
    return () => {};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasScript, listing.id]);
  if (!hasScript) {
    return null;
  }

  /* eslint-disable react/no-unknown-property */
  return /*#__PURE__*/_jsx("div", {
    id: "owner-chat-container-activengage",
    "chat-launch": "plainbutton",
    "chat-onavailable": "AE.onAvailable",
    "chat-onunavailable": "AE.onUnavailable",
    "chat-onlaunch": "AE.onLaunch",
    className: uiContext === 'snapshot' ? 'display-flex' : 'display-inline display-sm-block',
    children: showChatCTA ? renderButton({
      className,
      onChatClick,
      uiContext,
      isDealerNameRelocation,
      forceChatLaunch,
      ...rest
    }) : _span || (_span = /*#__PURE__*/_jsx("span", {}))
  }, `owner-chat-${listing.id}`);
  /* eslint-enable react/no-unknown-property */
}, (prev, next) => prev.listing?.id === next.listing?.id);
export default OwnerChatActivEngage;